import { ErrorMessage, Field, Form, Formik, FormikProps } from "formik";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { ButtonCustom } from "../../../../components/custom/button-custom";
import { ModalCustom } from "../../../../components/custom/modal-custom";

import { showToast } from "../../../../components/custom/custom-toast";
import { LoadingCustom } from "../../../../components/custom/loading-custom";
import { SelectOneDropdown } from "../../../../components/custom/select-one-dropdown";
import { dataAppType } from "../../../../constants/status";
import { useFetchData } from "../../../../hooks/useFetch";
import { AppVersionResponse } from "../../../../models/response/app-version";
import { API_APP_VERSION_ROUTER } from "../../../../services/api-config";
import { isSuccess } from "../../../../services/base-response";

interface AppVersionUpdateProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  setRefresh: (value: boolean) => void;
  dataObject: AppVersionResponse;
}

export const AppVersionUpdate = ({ isOpen, setIsOpen, setRefresh, dataObject }: AppVersionUpdateProps) => {
  const formikRef = useRef<FormikProps<any> | null>(null);
  const [dataAppTypes, setDataAppTypes] = useState<any>([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const { request: fetchDataUpdate, baseResponse: responsesUpdate } = useFetchData.Post();

  useEffect(() => {
    setDataAppTypes(() => {
      return {
        id: `${dataObject.app_type},${dataObject.platform}`, // Ghép chuỗi để tạo id
        version: dataObject.version,
        is_force_update: dataObject.is_force_update,
      };
    });
  }, [dataObject]);

  useEffect(() => {
    if (!isOpen) formikRef.current?.resetForm();
  }, [isOpen]);

  const handleSubmit = (values: any) => {
    console.log("values", values);
    const selectIdArray = values.id.split(",");
    const app_type = parseInt(selectIdArray[0], 10);
    const platform = parseInt(selectIdArray[1], 10);

    fetchDataUpdate({
      endPoint: API_APP_VERSION_ROUTER.API_UPDATE_APP_VERSION(dataObject._id),
      data: {
        app_type: app_type,
        platform: platform,
        version: values.version,
        is_force_update: values.is_force_update,
      },
    });
  };

  useEffect(() => {
    if (!responsesUpdate) return;
    if (isSuccess(responsesUpdate)) {
      showToast({ message: "Chỉnh sửa thành công", type: "success" });
      setRefresh(true);
      setIsOpen(false);
    } else {
      showToast({ message: responsesUpdate.message, type: "error" });
    }
  }, [responsesUpdate]);

  useEffect(() => {
    if (dataObject._id !== "") setIsDataLoaded(true); // Mark data as fully loaded when both profile and branch data are available
  }, [dataObject]);

  if (!isDataLoaded) return <LoadingCustom />;

  const SubmittedForm = Yup.object().shape({
    version: Yup.string().required("Vui lòng nhập version"),
    id: Yup.string().min(1, "Ít nhất 1 phiên bản ").required("Vui lòng chọn phiên bản"),
  });

  return (
    <Formik
      innerRef={formikRef}
      initialValues={dataAppTypes}
      validationSchema={SubmittedForm}
      onSubmit={(values) => {
        handleSubmit(values);
      }}
    >
      {({ values, submitForm, resetForm, setFieldValue, errors }) => {
        const bodyModal = () => {
          return (
            <Form className="">
              <div className="flex flex-wrap items-start gap-4">
                <div className="w-full bg-white p-4 pt-0 rounded-md">
                  <div className="flex flex-col gap-2">
                    <div>
                      <label htmlFor="version">
                        Phiên bản <span className="text-red-500">*</span>
                      </label>
                      <Field
                        value={values.version}
                        name="version"
                        type="text"
                        placeholder="Nhập thông tin"
                        className="form-input"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const inputValue = e.target.value;
                          setFieldValue("version", inputValue);
                        }}
                      />
                      <ErrorMessage name="version" component="div" className="text-red-500 text-xs mt-1" />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label htmlFor="id">Ứng dụng</label>
                    <Field name="selecidtId">
                      {() => (
                        <SelectOneDropdown
                          selectdId={values.id}
                          placeholder="Chọn ứng dụng"
                          dataOptions={dataAppType}
                          getOptionLabel={(option: any) => option.name}
                          getOptionKey={(option) => option.id}
                          onChange={(selectedOption) => {
                            setFieldValue("id", selectedOption ? selectedOption.id : "");
                          }}
                        />
                      )}
                    </Field>
                    <ErrorMessage name="id" component="div" className="text-red-500 text-xs mt-1" />
                  </div>
                  <div className="flex justify-start items-center mt-7">
                    <Field
                      type="checkbox"
                      name="is_force_update"
                      className="form-checkbox"
                      checked={values.is_force_update}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFieldValue("is_force_update", e.target.checked ? 1 : 0);
                      }}
                    />
                    <span className="ml-2">Yêu cầu cập nhật</span>
                  </div>
                </div>
              </div>
            </Form>
          );
        };

        const footerModal = () => (
          <>
            <ButtonCustom
              type="button"
              text="Hủy"
              onClick={() => {
                resetForm();
                setIsOpen(false);
              }}
              className="bg-gray-100 text-gray-500 hover:bg-gray-400 hover:text-white"
            />
            <ButtonCustom
              type="button"
              text="Lưu"
              onClick={() => {
                submitForm();
                console.log("errors", errors);
              }}
            />
          </>
        );

        return (
          <ModalCustom
            data-testid="modal-custom"
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            titleModal="Thêm mới phiên bản"
            bodyModal={bodyModal()}
            footerModal={footerModal()}
            modalSize="40%"
          />
        );
      }}
    </Formik>
  );
};
